import { get, partition, pick } from 'lodash'
import { useEffect, useState } from 'react'
import { IntlShape, useIntl } from 'react-intl'

import { Attribute, DropdownAttribute, SEARCH_TYPES } from '@/components/Form/AudienceTargeting/Audience/interfaces'
import messages from '@/components/Form/AudienceTargeting/messages'
import { getScimExpression } from '@/components/Form/AudienceTargeting/utils'
import { withField, withFieldProps } from '@/components/Form/FieldWrappers/withField'
import ListBox from '@/components/GenericForm/ListBox'
import { useAppSelector } from '@/store/hooks'
import { targetingRuleSetsPredicate } from '@/utilities/UserUtilities'
import { getAttributes, getTargetingAttributes } from '@/utilities/UserUtilities/selectors'

import AttributeDropdown from './AttributeDropdown'
import AttributeSearch from './AttributeSearch'

const attributesToSave = ['id', 'displayName', 'type', 'urn', 'scimAttributeName']

const addAdditionalSearchTypes = (
  existing: DropdownAttribute[],
  intl: IntlShape,
  isFeatureEnabled: boolean,
  hasVariables: boolean,
  showCollectionEmailsOption: boolean
): DropdownAttribute[] => {
  const searchFilters = []
  if (isFeatureEnabled) {
    searchFilters.push({
      displayName: intl.formatMessage(messages.individual),
      placeholder: intl.formatMessage(messages.individualPlaceholder),
      id: 'individual',
      type: SEARCH_TYPES.search
    })
  }
  if (hasVariables && showCollectionEmailsOption) {
    searchFilters.push({
      displayName: 'List of emails',
      placeholder: 'select list of emails',
      id: 'collectionEmails',
      type: SEARCH_TYPES.collectionEmailsSearch
    })
  }

  return [...searchFilters, ...existing]
}

interface FilterProps {
  name: string
  variables?: []
  readOnly?: boolean
  showCollectionEmailsOption?: boolean
}
type SavedAttribute = Pick<Attribute, 'id' | 'displayName' | 'type' | 'urn' | 'scimAttributeName'>

const Filter = ({
  name,
  value: filterData = {},
  variables,
  readOnly = false,
  form,
  showCollectionEmailsOption
}: FilterProps & withFieldProps) => {
  const intl = useIntl()

  const { operator, value } = filterData
  const attribute: SavedAttribute = filterData.attribute

  const [aggregateAttributes, setAggregateAttributes] = useState<DropdownAttribute[]>([])

  const [selectedAttribute, setSelectedAttribute] = useState<DropdownAttribute>()
  const allAttributes: Attribute[] = useAppSelector(getAttributes)
  const targetingAttributes: Attribute[] = useAppSelector(getTargetingAttributes)
  const { setFieldValue, values } = form

  const ruleSetAttributes = allAttributes.filter(targetingRuleSetsPredicate)
  const [, simpleAttributes] = partition(ruleSetAttributes, 'scimParentAttributeName')

  useEffect(() => {
    if (!selectedAttribute && attribute) {
      const foundAttribute = aggregateAttributes.find(
        attr => attr.id === attribute.id || attr?.attribute?.urn === attribute.urn
      )
      setSelectedAttribute(foundAttribute)
    }
  }, [aggregateAttributes, attribute, selectedAttribute])

  useEffect(() => {
    if (attribute) {
      // TODO Update this when we refactor this audience targeting, this will break when we add more than just groups
      // Ideally we would loop through all of the children of a complex value to find the sub-values that are targetable
      let complexUrn = attribute.urn
      const attributeType = attribute.type
      if (attribute.urn === 'urn:ietf:params:scim:schemas:core:2.0:User:groups') {
        complexUrn += '.value'
      }
      if (attribute.urn === 'urn:ietf:params:scim:schemas:core:2.0:User:emails') {
        complexUrn += '.value'
      }
      if (attribute.id === 'collectionEmails') {
        setFieldValue(`${name}.scimExpression`, { type: 'collectionEmails', value })
      } else {
        const expression = getScimExpression(complexUrn, value, operator, attributeType)
        setFieldValue(`${name}.scimExpression`, expression)
      }
    }
  }, [attribute, name, operator, setFieldValue, value])

  useEffect(() => {
    // Feature flag to prevent Groups from appearing before we are ready
    const aggregateAttributes = allAttributes.filter(attr => attr.targetable && attr.scimAttributeName !== 'name')

    const mapped: DropdownAttribute[] = aggregateAttributes.map(attr => ({
      attribute: attr,
      type: SEARCH_TYPES.aggregate,
      id: attr.id,
      displayName: attr.displayName
    }))

    setAggregateAttributes(
      // @ts-expect-error
      addAdditionalSearchTypes(mapped, intl, true, variables?.length > 0, showCollectionEmailsOption)
    )
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [allAttributes, targetingAttributes, intl, showCollectionEmailsOption])

  const searchBoxValue = {
    displayValue: get(values, `${name}.displayValue`),
    value: get(values, `${name}.value`),
    subtitle: get(values, `${name}.ruleTextDisplayValue`)
  }
  return (
    <div className="row">
      <div className="column no-grow">
        <ListBox
          disabled={readOnly}
          onChange={option => setFieldValue(`${name}.operator`, option.value)}
          name={`${name}.operator`}
          value={get(values, `${name}.operator`)}
          options={[
            {
              label: intl.formatMessage(messages.includes),
              value: 'eq'
            },
            {
              label: intl.formatMessage(messages.excludes),
              value: 'ne'
            }
          ]}
        />
      </div>
      <div className="ml-2">
        <AttributeDropdown
          readOnly={readOnly}
          name={`${name}.attribute`}
          onAttributeChange={(values: any) => {
            // Remove value
            setSelectedAttribute(values)
            setFieldValue(`${name}.attribute`, values)
            setFieldValue(`${name}.value`, '')
            setFieldValue(`${name}.displayValue`, '')
          }}
          attributeOptions={aggregateAttributes}
          selectedAttribute={selectedAttribute}
          renderErrors={!selectedAttribute}
        />
      </div>

      {selectedAttribute && selectedAttribute.id && (
        <>
          <div className="equals-sign items-center text-center">{'='}</div>
          <div className={`column ${variables ? 'no-shrink' : 'no-grow'}`}>
            <AttributeSearch
              readOnly={readOnly}
              variables={variables}
              initialValue={searchBoxValue}
              onSelect={(value, attribute) => {
                setFieldValue(`${name}.attribute`, pick(attribute, attributesToSave))
                setFieldValue(`${name}.value`, value.value)
                setFieldValue(`${name}.displayValue`, value.displayValue || value.value)
                setFieldValue(
                  `${name}.ruleTextDisplayValue`,
                  variables ? value.subtitle : value.displayValue || value.value
                )
              }}
              name={`${name}.value`}
              aggregateAttributes={simpleAttributes}
              attribute={selectedAttribute}
            />
          </div>
        </>
      )}
    </div>
  )
}

export default withField(Filter)
